/**
 * `<FeatureNotAvailableModal>` — global listener for
 * `feature-not-available` custom events. Two emit paths:
 *
 *   1. Backend 403 with `error: "feature_not_available"` body
 *      (AiVisibilityGate, COMPETITOR_BENCHMARKING gate, etc.)
 *      via the Axios interceptor in `lib/api.ts`.
 *   2. Frontend `<UpgradeLockedWrapper>` click handler when a
 *      Starter user clicks a locked CTA proactively gated by
 *      `useFeatureAccess`.
 *
 * Both paths converge on the same modal — one upgrade CTA copy,
 * one navigation flow, no duplication. Mirrors the
 * `TopupPickerModal` pattern (which listens for
 * `plan-limit-exceeded`) so the global event-driven upgrade-flow
 * shape stays consistent.
 *
 * Mount once in DashboardLayout; renders nothing until an event
 * fires.
 */
import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModalOverlay } from '@/components/posts/ModalPrimitives';
import { Button } from '@/components/ui/Button';

interface FeatureNotAvailableDetail {
  error?: string;
  feature?: string;
  featureName?: string;
  upgradeUrl?: string;
}

export function FeatureNotAvailableModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<FeatureNotAvailableDetail | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<FeatureNotAvailableDetail>;
      setDetail(customEvent.detail);
    };
    window.addEventListener('feature-not-available', handler);
    return () => window.removeEventListener('feature-not-available', handler);
  }, []);

  if (!detail) return null;

  const close = () => setDetail(null);

  const goToBilling = () => {
    const url = detail.upgradeUrl ?? '/dashboard/settings/billing';
    setDetail(null);
    navigate(url);
  };

  return (
    <ModalOverlay onClose={close}>
      <div
        data-testid="feature-not-available-modal"
        className="bg-card relative w-full max-w-md rounded-2xl shadow-2xl"
      >
        <div className="border-border flex items-start gap-3 border-b px-6 pt-6 pb-4">
          <div className="bg-accent/15 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg">
            <Lock className="text-accent h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-text-primary text-base font-semibold">
              {t('errors.featureNotAvailable')}
            </h2>
            {detail.featureName && (
              <p className="text-text-secondary mt-0.5 text-sm">{detail.featureName}</p>
            )}
          </div>
        </div>
        <div className="px-6 pt-4 pb-6">
          <p className="text-text-secondary text-sm">{t('errors.featureUpgradeHint')}</p>
          <div className="mt-5 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={close} className="px-3 py-2 text-sm">
              {t('common.notNow')}
            </Button>
            <Button
              onClick={goToBilling}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm"
            >
              {t('errors.viewPlans')}
            </Button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
